export type Lang = 'en' | 'es'

export const translations: Record<Lang, Record<string, string>> = {
  en: {
    // Chat
    'chat.open': 'Open chat',
    'chat.assistant': 'Assistant',
    'chat.minimize': 'Minimize',
    'chat.close': 'Close',
    'chat.title': 'Ask the Agent',
    'chat.reset': 'Reset Chat',
    'chat.thinking': 'Thinking...',
    'chat.sources': 'Sources',
    'chat.placeholder': 'Ask about your infrastructure...',
    'chat.note': 'AI responses powered by configured LLM provider',
    'chat.welcome': "Hello! I'm your data center assistant. I can help you analyze infrastructure metrics, PUE data, and provide insights about your DCIM systems. What would you like to know?",
    'chat.cleared': 'Chat cleared! How can I help you today?',
    'chat.error.send': 'Failed to send message',
    'chat.error.unexpected': 'An unexpected error occurred',
    'chat.error.prefix': 'Sorry, I encountered an error:',
    'chat.error.retry': 'Please try again.',
    'chat.error.loadHistory': 'Failed to load conversation history',
    'chat.conversations': 'Conversations',
    'chat.newConversation': 'New Conversation',
    'chat.noConversations': 'No conversations yet',
    'chat.confirmDelete': 'Delete this conversation?',
    'chat.deleteConversation': 'Delete conversation',
    'chat.loadingHistory': 'Loading conversation history...',
    'chat.loading': 'Loading...',
    'chat.progress.searching': 'Searching for data in the database...',
    'chat.progress.processing': 'Processing your request, this may take a moment...',
    'chat.progress.analyzing': 'Still analyzing the data, please wait...',
    'chat.progress.finishing': 'Almost done, thank you for your patience...',
    // Header
    'header.subtitle': 'Powered by Data Evolution',
    'header.signout': 'Sign out',
    'header.supabase': 'Supabase',
    'header.analytics': 'PUE Analytics',

    // Landing
    'landing.tagline': 'Transform your data center management',
    'landing.cta': 'Access Dashboard',
    'landing.features.title': 'Powerful Features',
    'landing.features.subtitle': 'Everything you need to monitor, analyze, and optimize your data center operations',
    'landing.footer.version': 'Advanced Data Center Infrastructure Management Platform',
    'landing.footer.tags': 'Monitoring · Analytics · Optimization · AI-Powered Insights',
    'landing.stats.uptime': 'Uptime Monitoring',
    'landing.stats.analytics': 'Real-time Analytics',
    'landing.stats.integration': 'DCIM Integration',

    // Login
    'login.back': 'Back to Home',
    'login.title': 'Coolbit AI',
    'login.welcome': 'Welcome Back',
    'login.subtitle': 'Sign in to access your dashboard',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.signin': 'Sign In',
    'login.create': 'Create account',
    'login.reset': 'Forgot password',
    'login.banner': 'Use your email and password. If you don\'t have an account yet, click "Create account" below.',

    // Summary
    'summary.title': 'Database Summary',
    'summary.refresh': 'Refresh',
    'summary.error.title': 'Database Error',
    'summary.error.body': 'Failed to fetch database summary',
    'summary.retry': 'Retry',
    'summary.loading': 'Loading database summary...',
    'summary.totalRecords': 'Total Records',
    'summary.avgPue': 'Avg PUE (24h)',
    'summary.latest': 'Latest Data',
    'summary.lastTimestamp': 'Last Timestamp:',
    'summary.analysisPeriod': 'Analysis Period:',
    'summary.selectPeriod': 'Select Period:',
    'summary.dataSource': 'Data Source',
    'summary.schema': 'Schema:',
    'summary.table': 'Table:',
    'summary.sourceTable': 'Source Table:',
    'summary.queried': 'Queried:',
    'summary.pueExplainer': 'PUE (Power Usage Effectiveness) measures data center energy efficiency. Lower values indicate better efficiency.',
    // Health
    'health.title': 'System Health',
    'health.error.title': 'Health Check Failed',
    'health.error.body': 'Failed to fetch health status',
    'health.retry': 'Retry',
    'health.loading': 'Checking system health...',
    'health.overall': 'Overall Status',
    'health.environment': 'Environment:',
    'health.services': 'Services',
    'health.database': 'Database',
    'health.grafana': 'Grafana',
    'health.version': 'Version',
    'health.lastCheck': 'Last Check',
    'health.status.healthy': 'Healthy',
    'health.status.degraded': 'Degraded',
    'health.status.error': 'Error',
    'health.status.unknown': 'Unknown',
    // PUE Chart
    'pue.title': 'PUE Trend',
    'pue.subtitle': 'Power Usage Effectiveness from Supabase data',
    'pue.period.label': 'Time Range:',
    'pue.period.4h': 'Last 4 hours',
    'pue.period.12h': 'Last 12 hours',
    'pue.period.24h': 'Last 24 hours',
    'pue.period.3d': 'Last 3 days',
    'pue.period.1w': 'Last 7 days',
    'pue.period.1M': 'Last month',
    'pue.period.30d': 'Last 30 days',
    'pue.period.2M': 'Last 2 months',
    'pue.period.3M': 'Last 3 months',
    'pue.period.6M': 'Last 6 months',
    'pue.period.12M': 'Last 12 months',
    'pue.refresh': 'Refresh',
    'pue.error.title': 'Failed to load PUE data',
    'pue.error.retry': 'Try again',
    'pue.loading': 'Loading PUE data...',
    'pue.noData': 'No PUE data available yet',
    'pue.metrics.latest': 'Latest PUE',
    'pue.metrics.average': 'Average PUE',
    'pue.metrics.count': 'Data points',
    'pue.axisLabel': 'PUE',
    'pue.legend': 'PUE',
    'pue.updated': 'Updated',
    'pue.location': 'Location',

    // Temperature & Humidity Widget
    'tempHumidity.title': 'Temperature & Humidity Trends',
    'tempHumidity.subtitle': 'Environmental monitoring over time',
    'tempHumidity.loading': 'Loading temperature and humidity data...',
    'tempHumidity.noData': 'No environmental data available yet',
    'tempHumidity.error.title': 'Failed to load environmental data',
    'tempHumidity.metrics.latestTemp': 'Latest Temp',
    'tempHumidity.metrics.avgTemp': 'Avg Temperature',
    'tempHumidity.metrics.latestHumidity': 'Latest Humidity',
    'tempHumidity.metrics.avgHumidity': 'Avg Humidity',
    'tempHumidity.tempAxis': 'Temperature (°C)',
    'tempHumidity.humidityAxis': 'Humidity (%)',
    'tempHumidity.tempLegend': 'Temperature',
    'tempHumidity.humidityLegend': 'Humidity',

    // Power Analysis Widget
    'powerAnalysis.title': 'Power Consumption Analysis',
    'powerAnalysis.subtitle': 'IT vs Cooling power trends with PUE correlation',
    'powerAnalysis.loading': 'Loading power analysis data...',
    'powerAnalysis.noData': 'No power data available yet',
    'powerAnalysis.error.title': 'Failed to load power analysis data',
    'powerAnalysis.metrics.latestIT': 'Latest IT Power',
    'powerAnalysis.metrics.latestCooling': 'Latest Cooling',
    'powerAnalysis.metrics.efficiency': 'Cooling Ratio',
    'powerAnalysis.metrics.totalPower': 'Total Power',
    'powerAnalysis.powerAxis': 'Power (kW)',
    'powerAnalysis.pueAxis': 'PUE',
    'powerAnalysis.itPowerLegend': 'IT Power',
    'powerAnalysis.coolingPowerLegend': 'Cooling Power',
    'powerAnalysis.pueLegend': 'PUE',

    // Analytics Container
    'analytics.title': 'Infrastructure Analytics',
    'analytics.subtitle': 'Real-time monitoring and trend analysis',
    'analytics.refreshAll': 'Refresh All',
    'analytics.footer.info': 'All data points synchronized',
    'analytics.footer.period': 'Current period',

    // Location & Compliance Widget
    'locationCompliance.title': 'Location & Compliance Overview',
    'locationCompliance.subtitle': 'Distribution analysis and compliance tracking',
    'locationCompliance.loading': 'Loading location and compliance data...',
    'locationCompliance.noData': 'No location/compliance data available yet',
    'locationCompliance.error.title': 'Failed to load location/compliance data',
    'locationCompliance.metrics.locations': 'Total Locations',
    'locationCompliance.metrics.vendors': 'Vendors',
    'locationCompliance.metrics.dataPoints': 'Data Points',
    'locationCompliance.locationDistribution': 'Location Distribution',
    'locationCompliance.complianceStatus': 'Compliance Status',
    'locationCompliance.period': 'Period',

    // System Info Container
    'systemInfo.title': 'System Information',
    'systemInfo.subtitle': 'Health status and database summary',
    'systemInfo.footer.info': 'System monitoring active',
    'systemInfo.footer.status': 'All systems operational',

    // Schneider
    // Schneider
    'sch.title': 'Schneider EcoStruxure',
    'sch.rackId': 'Rack ID',
    'sch.location': 'Location',
    'sch.limit': 'Limit',
    'sch.period': 'Period (summary)',
    'sch.filters.active': 'Filters active',
    'sch.filters.none': 'No filter (showing recent)',
    'sch.apply': 'Apply',
    'sch.clear': 'Clear',
    'sch.kpi.total': 'Total Records',
    'sch.kpi.avgTemp': 'Avg Temp (°C)',
    'sch.kpi.avgPower': 'Avg Power (kW)',
    'sch.table.timestamp': 'Timestamp',
    'sch.table.rack': 'Rack',
    'sch.table.location': 'Location',
    'sch.table.temp': 'Temp (°C)',
    'sch.table.humidity': 'Humidity (%)',
    'sch.table.power': 'Power (kW)',
    'sch.table.noData': 'No data',
    'sch.error.summary': 'Failed to fetch Schneider summary',
    'sch.error.filtered': 'Failed to fetch filtered Schneider data',
    'sch.error.recent': 'Failed to fetch recent Schneider data',
    // Dashboard
    'dashboard.title': 'Dashboard',
    'dashboard.subtitle': 'Monitor your data center infrastructure and analytics',

    // Monthly Cost Widget
    'monthlyCost.title': 'Monthly Cost per Tenant (Rack 5 KW)',
    'monthlyCost.subtitle': 'Regulated Values 2025',
    'monthlyCost.energy': 'Energy (KWh)',
    'monthlyCost.demand': 'Demand (KW) Components',
    'monthlyCost.additionalCharge': 'Additional Charge (KW)',
    'monthlyCost.total': 'Monthly Total:',
    'monthlyCost.currency': 'MXN Pesos'
  },
  es: {
    // Chat
    'chat.open': 'Abrir chat',
    'chat.assistant': 'Asistente',
    'chat.minimize': 'Minimizar',
    'chat.close': 'Cerrar',
    'chat.title': 'Pregunta al Agente',
    'chat.reset': 'Reiniciar Chat',
    'chat.thinking': 'Pensando...',
    'chat.sources': 'Fuentes',
    'chat.placeholder': 'Pregunta sobre tu infraestructura...',
    'chat.note': 'Respuestas de IA alimentadas por el proveedor LLM configurado',
    'chat.welcome': '¡Hola! Soy tu asistente del centro de datos. Puedo ayudarte a analizar métricas de infraestructura, datos de PUE y ofrecer ideas sobre tus sistemas DCIM. ¿Qué te gustaría saber?',
    'chat.cleared': '¡Chat reiniciado! ¿Cómo puedo ayudarte hoy?',
    'chat.error.send': 'No se pudo enviar el mensaje',
    'chat.error.unexpected': 'Ocurrió un error inesperado',
    'chat.error.prefix': 'Lo siento, encontré un error:',
    'chat.error.retry': 'Inténtalo de nuevo.',
    'chat.error.loadHistory': 'No se pudo cargar el historial de conversación',
    'chat.conversations': 'Conversaciones',
    'chat.newConversation': 'Nueva Conversación',
    'chat.noConversations': 'Sin conversaciones aún',
    'chat.confirmDelete': '¿Eliminar esta conversación?',
    'chat.deleteConversation': 'Eliminar conversación',
    'chat.loadingHistory': 'Cargando historial de conversación...',
    'chat.loading': 'Cargando...',
    'chat.progress.searching': 'Buscando datos en la base de datos...',
    'chat.progress.processing': 'Procesando tu solicitud, esto puede tomar un momento...',
    'chat.progress.analyzing': 'Aún analizando los datos, por favor espera...',
    'chat.progress.finishing': 'Casi terminado, gracias por tu paciencia...',
    // Header
    'header.subtitle': 'Powered by Data Evolution',
    'header.signout': 'Cerrar sesión',
    'header.supabase': 'Supabase',
    'header.analytics': 'Analítica PUE',

    // Landing
    'landing.tagline': 'Transforma la gestión de tu centro de datos',
    'landing.cta': 'Acceder al Panel',
    'landing.features.title': 'Funciones Potentes',
    'landing.features.subtitle': 'Todo lo que necesitas para monitorear, analizar y optimizar las operaciones de tu centro de datos',
    'landing.footer.version': 'Plataforma avanzada de gestión de infraestructura de centros de datos',
    'landing.footer.tags': 'Monitoreo · Analítica · Optimización · IA',
    'landing.stats.uptime': 'Monitoreo de Disponibilidad',
    'landing.stats.analytics': 'Analítica en Tiempo Real',
    'landing.stats.integration': 'Integración DCIM',

    // Login
    'login.back': 'Volver al inicio',
    'login.title': 'Coolbit AI',
    'login.welcome': 'Bienvenido',
    'login.subtitle': 'Inicia sesión para acceder a tu panel',
    'login.email': 'Correo electrónico',
    'login.password': 'Contraseña',
    'login.signin': 'Iniciar sesión',
    'login.create': 'Crear cuenta',
    'login.reset': '¿Olvidaste tu contraseña?',
    'login.banner': 'Usa tu correo y contraseña. Si aún no tienes cuenta, pulsa "Crear cuenta" abajo.',

    // Summary
    'summary.title': 'Resumen de Base de Datos',
    'summary.refresh': 'Actualizar',
    'summary.error.title': 'Error de Base de Datos',
    'summary.error.body': 'No se pudo obtener el resumen de la base de datos',
    'summary.retry': 'Reintentar',
    'summary.loading': 'Cargando resumen de base de datos...',
    'summary.totalRecords': 'Registros Totales',
    'summary.avgPue': 'PUE Promedio (24h)',
    'summary.latest': 'Datos Recientes',
    'summary.lastTimestamp': 'Última Marca de Tiempo:',
    'summary.analysisPeriod': 'Periodo de Análisis:',
    'summary.selectPeriod': 'Seleccionar Periodo:',
    'summary.dataSource': 'Fuente de Datos',
    'summary.schema': 'Esquema:',
    'summary.table': 'Tabla:',
    'summary.sourceTable': 'Tabla de origen:',
    'summary.queried': 'Consultado:',
    'summary.pueExplainer': 'PUE (Eficiencia en el Uso de Energía) mide la eficiencia energética del centro de datos. Valores menores indican mejor eficiencia.',
    // Health
    'health.title': 'Salud del Sistema',
    'health.error.title': 'Fallo en la verificación de salud',
    'health.error.body': 'No se pudo obtener el estado de salud',
    'health.retry': 'Reintentar',
    'health.loading': 'Comprobando la salud del sistema...',
    'health.overall': 'Estado general',
    'health.environment': 'Entorno:',
    'health.services': 'Servicios',
    'health.database': 'Base de datos',
    'health.grafana': 'Grafana',
    'health.version': 'Versión',
    'health.lastCheck': 'Última verificación',
    'health.status.healthy': 'Saludable',
    'health.status.degraded': 'Degradado',
    'health.status.error': 'Error',
    'health.status.unknown': 'Desconocido',
    // PUE Chart
    'pue.title': 'Tendencia de PUE',
    'pue.subtitle': 'Eficiencia energética desde Supabase',
    'pue.period.label': 'Rango:',
    'pue.period.4h': 'Últimas 4 horas',
    'pue.period.12h': 'Últimas 12 horas',
    'pue.period.24h': 'Últimas 24 horas',
    'pue.period.3d': 'Últimos 3 días',
    'pue.period.1w': 'Últimos 7 días',
    'pue.period.1M': 'Último mes',
    'pue.period.30d': 'Últimos 30 días',
    'pue.period.2M': 'Últimos 2 meses',
    'pue.period.3M': 'Últimos 3 meses',
    'pue.period.6M': 'Últimos 6 meses',
    'pue.period.12M': 'Últimos 12 meses',
    'pue.refresh': 'Actualizar',
    'pue.error.title': 'No se pudieron cargar los datos de PUE',
    'pue.error.retry': 'Intentar de nuevo',
    'pue.loading': 'Cargando datos de PUE...',
    'pue.noData': 'Aún no hay datos de PUE disponibles',
    'pue.metrics.latest': 'PUE más reciente',
    'pue.metrics.average': 'PUE promedio',
    'pue.metrics.count': 'Puntos de datos',
    'pue.axisLabel': 'PUE',
    'pue.legend': 'PUE',
    'pue.updated': 'Actualizado',
    'pue.location': 'Ubicación',

    // Temperature & Humidity Widget
    'tempHumidity.title': 'Tendencias de Temperatura y Humedad',
    'tempHumidity.subtitle': 'Monitoreo ambiental a lo largo del tiempo',
    'tempHumidity.loading': 'Cargando datos de temperatura y humedad...',
    'tempHumidity.noData': 'Aún no hay datos ambientales disponibles',
    'tempHumidity.error.title': 'No se pudieron cargar los datos ambientales',
    'tempHumidity.metrics.latestTemp': 'Temp. Actual',
    'tempHumidity.metrics.avgTemp': 'Temp. Promedio',
    'tempHumidity.metrics.latestHumidity': 'Humedad Actual',
    'tempHumidity.metrics.avgHumidity': 'Humedad Promedio',
    'tempHumidity.tempAxis': 'Temperatura (°C)',
    'tempHumidity.humidityAxis': 'Humedad (%)',
    'tempHumidity.tempLegend': 'Temperatura',
    'tempHumidity.humidityLegend': 'Humedad',

    // Power Analysis Widget
    'powerAnalysis.title': 'Análisis de Consumo de Energía',
    'powerAnalysis.subtitle': 'Tendencias de energía IT vs refrigeración con correlación PUE',
    'powerAnalysis.loading': 'Cargando datos de análisis de energía...',
    'powerAnalysis.noData': 'Aún no hay datos de energía disponibles',
    'powerAnalysis.error.title': 'No se pudieron cargar los datos de análisis de energía',
    'powerAnalysis.metrics.latestIT': 'Energía IT Actual',
    'powerAnalysis.metrics.latestCooling': 'Refrigeración Actual',
    'powerAnalysis.metrics.efficiency': 'Ratio Refrigeración',
    'powerAnalysis.metrics.totalPower': 'Energía Total',
    'powerAnalysis.powerAxis': 'Energía (kW)',
    'powerAnalysis.pueAxis': 'PUE',
    'powerAnalysis.itPowerLegend': 'Energía IT',
    'powerAnalysis.coolingPowerLegend': 'Energía Refrigeración',
    'powerAnalysis.pueLegend': 'PUE',

    // Analytics Container
    'analytics.title': 'Análisis de Infraestructura',
    'analytics.subtitle': 'Monitoreo en tiempo real y análisis de tendencias',
    'analytics.refreshAll': 'Actualizar Todo',
    'analytics.footer.info': 'Todos los puntos de datos sincronizados',
    'analytics.footer.period': 'Período actual',

    // Location & Compliance Widget
    'locationCompliance.title': 'Resumen de Ubicación y Cumplimiento',
    'locationCompliance.subtitle': 'Análisis de distribución y seguimiento de cumplimiento',
    'locationCompliance.loading': 'Cargando datos de ubicación y cumplimiento...',
    'locationCompliance.noData': 'Aún no hay datos de ubicación/cumplimiento disponibles',
    'locationCompliance.error.title': 'No se pudieron cargar los datos de ubicación/cumplimiento',
    'locationCompliance.metrics.locations': 'Ubicaciones Totales',
    'locationCompliance.metrics.vendors': 'Proveedores',
    'locationCompliance.metrics.dataPoints': 'Puntos de Datos',
    'locationCompliance.locationDistribution': 'Distribución por Ubicación',
    'locationCompliance.complianceStatus': 'Estado de Cumplimiento',
    'locationCompliance.period': 'Período',

    // System Info Container
    'systemInfo.title': 'Información del Sistema',
    'systemInfo.subtitle': 'Estado de salud y resumen de base de datos',
    'systemInfo.footer.info': 'Monitoreo del sistema activo',
    'systemInfo.footer.status': 'Todos los sistemas operativos',

    // Schneider
    // Schneider
    'sch.title': 'Schneider EcoStruxure',
    'sch.rackId': 'ID de Rack',
    'sch.location': 'Ubicación',
    'sch.limit': 'Límite',
    'sch.period': 'Periodo (resumen)',
    'sch.filters.active': 'Filtros activos',
    'sch.filters.none': 'Sin filtro (mostrando recientes)',
    'sch.apply': 'Aplicar',
    'sch.clear': 'Limpiar',
    'sch.kpi.total': 'Registros totales',
    'sch.kpi.avgTemp': 'Temp prom. (°C)',
    'sch.kpi.avgPower': 'Potencia prom. (kW)',
    'sch.table.timestamp': 'Marca de tiempo',
    'sch.table.rack': 'Rack',
    'sch.table.location': 'Ubicación',
    'sch.table.temp': 'Temp (°C)',
    'sch.table.humidity': 'Humedad (%)',
    'sch.table.power': 'Potencia (kW)',
    'sch.table.noData': 'Sin datos',
    'sch.error.summary': 'No se pudo obtener el resumen de Schneider',
    'sch.error.filtered': 'No se pudieron obtener los datos filtrados de Schneider',
    'sch.error.recent': 'No se pudieron obtener los datos recientes de Schneider',
    // Dashboard
    'dashboard.title': 'Panel',
    'dashboard.subtitle': 'Supervisa la infraestructura y analíticas de tu centro de datos',

    // Monthly Cost Widget
    'monthlyCost.title': 'Costo mensual por inquilino (Rack 5 KW)',
    'monthlyCost.subtitle': 'Valores Regulados 2025',
    'monthlyCost.energy': 'Energía (KWh)',
    'monthlyCost.demand': 'Demanda (KW) Componentes',
    'monthlyCost.additionalCharge': 'Cargo adicional (KW)',
    'monthlyCost.total': 'Total mensual:',
    'monthlyCost.currency': 'Pesos MXN'
  }
}
